'use strict';

/**
 * T1496A / S333 W2 — the ONE message both ends of the identity-bin
 * proof-of-unwrap sign and verify.
 *
 * Browser + Node (no Buffer, no node:crypto): the browser signs this inside
 * the vault bundle (shared/crypto/identity-bin-proof-client.js), the server
 * verifies it at the reseal sink (auth/identity-bin-proof.js). ONE builder,
 * required by both, so the two runtimes cannot drift into twins.
 *
 * WHAT IS BOUND, AND WHY EACH FIELD IS THERE:
 *   - PROOF_DOMAIN   — domain separation: a signature minted here can never
 *                      double as a JIT grant, a device assertion or anything
 *                      else the same keypair might sign.
 *   - accountNumber  — the nonce is account-bound at the store; binding the
 *                      account into the message too means a signature cannot
 *                      be re-targeted even if a store ever lost that binding.
 *   - nonce          — server-issued, single-use, short-lived (the replay and
 *                      freshness controls live in the store; the signature
 *                      proves THIS nonce was signed by the vault key).
 *   - envelope digest — sha256 over EXACTLY the four envelope fields the sink
 *                      persists ({algo, iv, ct, tag}). A captured signature
 *                      therefore authorises ONE ciphertext, never "any write
 *                      while the nonce is live". Fields outside the four are
 *                      not signed — and the sink does not persist them either.
 *
 * ENCODING: JSON arrays of strings, not newline-joined fields — a JSON string
 * is escaped, so no field value can forge a delimiter. The outer message is
 * `JSON.stringify([domain, accountNumber, nonce, digestHex])` as UTF-8 bytes.
 */

const { sha256 } = require('@noble/hashes/sha2.js');

const PROOF_DOMAIN = 'oyster:identity-bin-reseal:v1';
const PROOF_VERSION = 1;

const TEXT_ENCODER = new TextEncoder();

function bytesToHex(bytes) {
  let out = '';
  for (let i = 0; i < bytes.length; i += 1) out += bytes[i].toString(16).padStart(2, '0');
  return out;
}

function hexToBytes(hexStr) {
  if (typeof hexStr !== 'string' || hexStr.length % 2 !== 0 || !/^[0-9a-fA-F]*$/.test(hexStr)) return null;
  const out = new Uint8Array(hexStr.length / 2);
  for (let i = 0; i < out.length; i += 1) out[i] = parseInt(hexStr.slice(i * 2, i * 2 + 2), 16);
  return out;
}

/**
 * sha256 (hex) over the four persisted envelope fields, in a fixed order.
 * Throws on a non-object: the caller validates envelope shape before this.
 */
function envelopeDigestHex(vaultBin) {
  if (!vaultBin || typeof vaultBin !== 'object') {
    throw new TypeError('envelopeDigestHex: vaultBin must be an object');
  }
  const preimage = JSON.stringify([
    String(vaultBin.algo), String(vaultBin.iv), String(vaultBin.ct), String(vaultBin.tag),
  ]);
  return bytesToHex(sha256(TEXT_ENCODER.encode(preimage)));
}

/**
 * @param {{ accountNumber: string, nonce: string, vaultBin: object }} args
 * @returns {Uint8Array} the bytes to sign / verify
 */
function buildProofMessage({ accountNumber, nonce, vaultBin }) {
  if (typeof accountNumber !== 'string' || accountNumber.length === 0) {
    throw new TypeError('buildProofMessage: accountNumber must be a non-empty string');
  }
  if (typeof nonce !== 'string' || nonce.length === 0) {
    throw new TypeError('buildProofMessage: nonce must be a non-empty string');
  }
  const digest = envelopeDigestHex(vaultBin);
  return TEXT_ENCODER.encode(JSON.stringify([PROOF_DOMAIN, accountNumber, nonce, digest]));
}

module.exports = {
  PROOF_DOMAIN,
  PROOF_VERSION,
  buildProofMessage,
  envelopeDigestHex,
  bytesToHex,
  hexToBytes,
};
